import React, { useEffect } from 'react';
import { Grid, TextField } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  logError,
  formatPhoneNumber,
  unformatPhoneNumber,
  useImageUpload,
  phoneNumberSchema,
  showToast,
} from '../../utils';
import { useGetUserByIdQuery, useUpdateUserMutation } from './users-api-slice.ts';
import { EntityProfilePage, PhoneNumberField } from '../../components/form';
import { setCurrentUser } from './user-slice.ts';

// Define Zod schema for the My Profile form validation
const myProfileFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(250, 'First name is too long'),
  lastName: z.string().min(1, 'Last name is required').max(250, 'Last name is too long'),
  phone: phoneNumberSchema,
});

// Extract type from Zod schema
type MyProfileFormValues = z.infer<typeof myProfileFormSchema>;

const MyProfilePage: React.FC = () => {
  // Get current user from Redux store
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const currentUserId = currentUser?.id ?? '';
  const dispatch = useDispatch();

  // Use the shared image upload hook
  const {
    imageFile: avatarFile,
    imagePreview: avatarPreview,
    imageChanged: avatarChanged,
    handleImageChange: handleAvatarChange,
    resetImageState: resetAvatarState,
  } = useImageUpload();

  // Get user data
  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useGetUserByIdQuery(currentUserId, {
    skip: !currentUserId,
    refetchOnMountOrArgChange: true,
  });

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  // Set up React Hook Form with Zod resolver
  const form = useForm<MyProfileFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
    },
    resolver: zodResolver(myProfileFormSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const {
    control,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = form;

  // Use a combined dirty state that includes both form changes and avatar changes
  const hasChanges = isDirty || avatarChanged;

  // Handle form submission with React Hook Form
  const onSubmit = async (data: MyProfileFormValues) => {
    try {
      // Process phone number to store only digits
      const processedData = {
        ...data,
        phone: unformatPhoneNumber(data.phone),
      };

      await updateUser({
        id: currentUserId,
        ...processedData,
        // Keep the existing values that aren't being updated
        email: user?.email,
        role: user?.role,
        active: user?.active,
        manufacturerId: user?.manufacturerId,
        dealerId: user?.dealerId,
        avatarFile: avatarFile || undefined,
      }).unwrap();
      showToast('User successfully updated', 'success');

      const updatedUser = await refetch().unwrap();

      // Update the currentUser in the Redux store directly
      if (updatedUser) {
        dispatch(setCurrentUser(updatedUser));
      }

      resetAvatarState();
      return Promise.resolve();
    } catch (error) {
      logError('Error updating profile:', error);
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    if (currentUserId) {
      refetch();
    }
  }, [currentUserId, refetch]);

  // Update form when user data is loaded
  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone,
      });
    }
  }, [user, reset]);

  return (
    <EntityProfilePage
      title="My Profile"
      entity={user}
      isLoading={isLoading}
      error={error}
      isSaving={isUpdating}
      form={form}
      hasChanges={hasChanges}
      isValid={isValid}
      imageName="avatar"
      imageUrl={avatarPreview ?? user?.avatar}
      imageAlt={`${watch('firstName')} ${watch('lastName')}`}
      uploadButtonText="Upload Photo"
      roundedImage={true}
      onSubmit={onSubmit}
      onImageChange={handleAvatarChange}
    >
      <Grid container spacing={2}>
        {/* First name */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="firstName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label="First Name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                inputProps={{ maxLength: 250 }}
              />
            )}
          />
        </Grid>

        {/* Last name */}
        <Grid item xs={12} sm={6}>
          <Controller
            name="lastName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                fullWidth
                label="Last Name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                required
                inputProps={{ maxLength: 250 }}
              />
            )}
          />
        </Grid>

        {/* Email (read-only) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            value={user?.email ?? ''}
            InputProps={{
              readOnly: true,
            }}
            disabled
            helperText="Email cannot be changed. Contact an administrator if needed."
          />
        </Grid>

        {/* Phone */}
        <Grid item xs={12}>
          <PhoneNumberField
            name="phone"
            control={control}
            formatPhoneNumber={formatPhoneNumber}
            label="Phone Number"
            required
          />
        </Grid>
      </Grid>
    </EntityProfilePage>
  );
};

export default MyProfilePage;
